// src/components/main/produzione/assegnazioniGantt/GanttOrdiniLavoroCard.tsx

import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {DateTime} from "luxon";

import {RootState, useAppDispatch} from "../../../../../../../../redux/store";
import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {
    FiltriGantt,
    FiltriOrdiniLavoro,
    setFiltriGantt, setFiltriOrdiniLavoro
} from "../../../../../../../userPreference/userPreferenceSlice";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import {useTheme} from "@mui/material/styles";

// Anagrafiche
import {Commerciale} from "components/anagrafiche/anagraficheSlice";

import GanttOrdineLavoroItem from "./GanttOrdineLavoroItem";
import {
    OrdiniLavoroList,
    OrdineLavoro,
    STATI_ORDINE_LAVORO,
    getStatoOrdineLavoroColor
} from "../../../../ordiniLavoroTypes";
import {fetchOrdiniLavoro} from "../../../../ordiniLavoroSlice";
import Chip from "@mui/material/Chip";
import {DesktopDatePicker} from "@mui/x-date-pickers/DesktopDatePicker";
import Grid from "@mui/material/Grid";
import {convertToDateTime} from "../../../../../../../common/dateUtils/convertToDateTime";
import Menu from "@mui/material/Menu";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

// ============================================
// INTERFACES
// ============================================

interface GanttOrdiniLavoroCardProps {
    dataDa: DateTime;
    numeroSettimane: 1 | 2 | 3 | 4 | 6;
    tecnicheSelezionate: number[];
    ordineLavoroSelezionatoId?: number;
    onOrdineLavoroClick: (ordineLavoroId: number) => void;
}

// ============================================
// HELPERS
// ============================================

/** Ordine non completato con valore ancora da schedulare */
const isDaSchedulare = (ol: OrdineLavoro): boolean =>
    ol.stato !== "DONE" && (ol.valore_schedulato || 0) < (ol.valore_confermato || 0);

/** Ordine non completato ma già interamente schedulato */
const isSchedulato = (ol: OrdineLavoro): boolean =>
    ol.stato !== "DONE" && (ol.valore_schedulato || 0) >= (ol.valore_confermato || 0);

/** Ordine completato (prodotto) */
const isProdotto = (ol: OrdineLavoro): boolean =>
    ol.stato === "DONE";

// ============================================
// COMPONENT
// ============================================

const GanttOrdiniLavoroCard = ({
                                   numeroSettimane,
                                   tecnicheSelezionate,
                                   ordineLavoroSelezionatoId,
                                   onOrdineLavoroClick,
                               }: GanttOrdiniLavoroCardProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();

    // ============================================
    // SELECTORS
    // ============================================
    const filtriOrdiniLavoro = useSelector<RootState, FiltriOrdiniLavoro>(
        (state) => state.userPreferenceState.filtriOrdiniLavoro || {}
    );
    const mostraTutti = filtriOrdiniLavoro.mostraTuttiOrdiniGantt || false;

    const commerciali = useSelector<RootState, Commerciale[]>(
        (state) => state.anagraficheState?.commerciali?.results || []
    );

    const ordiniLavoroList = useSelector<RootState, OrdiniLavoroList>(
        (state) => state.ordiniLavoroState.ordiniLavoroList
    );

    const isLoading = ordiniLavoroList.errorsStack.status === ErrorStatusTypes.PENDING;

    // ============================================
    // EFFECTS
    // ============================================
    const [refresh, setRefresh] = useState(false);

    const [page, setPage] = useState<number>(1);
    const [numero_record, setNumeroRecord] = useState<number>(10);
    const [statiSelezionati, setStatiSelezionati] = useState<string[]>(
        filtriOrdiniLavoro.stato || ['CONF', 'ARR']
    );
    const toggleStato = (stato: string) => {
        setStatiSelezionati((prev) =>
            prev.includes(stato)
                ? prev.filter((s) => s !== stato)
                : [...prev, stato]
        );
        setRefresh(true)
    };
    const [anchorElStatoFilter, setAnchorElStatoFilter] = useState<null | HTMLElement>(null);

    const [commercialeSel, setCommercialeSel] = useState<number | undefined>(
        filtriOrdiniLavoro.commerciale_id
    );

    const [dataDa, setDataDa] = useState<DateTime | null>(
        convertToDateTime(filtriOrdiniLavoro.data_da) ||
        DateTime.now().startOf("week")
    );
    const [dataA, setDataA] = useState<DateTime | null>(
        convertToDateTime(filtriOrdiniLavoro.data_a) ||
        DateTime.now().endOf("week")
    );

    useEffect(() => {
        const dataDaStr = dataDa?.toFormat("yyyy-MM-dd");
        const dataAStr = dataA?.toFormat("yyyy-MM-dd");
        const nuoviFiltri: FiltriOrdiniLavoro = {
            ...filtriOrdiniLavoro,
            data_da: dataDaStr,
            data_a: dataAStr,
            tecnica_id: tecnicheSelezionate.length > 0 ? tecnicheSelezionate : undefined,
            commerciale_id: commercialeSel,
            stato: statiSelezionati,
            page,
            numero_record,
            ordering: undefined
        }
        dispatch(setFiltriOrdiniLavoro(nuoviFiltri));
        dispatch(fetchOrdiniLavoro({...nuoviFiltri, stagione_id: undefined}));
        setRefresh(false);
    }, [dispatch, tecnicheSelezionate, refresh]);

    // ============================================
    // COMPUTED
    // ============================================

    const ordiniDaSchedulare = useMemo(
        () => ordiniLavoroList.results.filter(isDaSchedulare),
        [ordiniLavoroList.results]
    );

    const ordiniSchedulati = useMemo(
        () => ordiniLavoroList.results.filter(isSchedulato),
        [ordiniLavoroList.results]
    );

    const ordiniProdotti = useMemo(
        () => ordiniLavoroList.results.filter(isProdotto),
        [ordiniLavoroList.results]
    );

    const countVisualizzati = mostraTutti
        ? ordiniLavoroList.results.length
        : ordiniDaSchedulare.length;

    // ============================================
    // HANDLERS
    // ============================================

    const handleToggleMostraTutti = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        dispatch(setFiltriOrdiniLavoro({...filtriOrdiniLavoro, mostraTuttiOrdiniGantt: checked}));
    };

    // ============================================
    // RENDER SEZIONE
    // ============================================

    const renderSezione = (
        titolo: string,
        ordini: OrdineLavoro[],
        colore: "warning" | "info" | "success",
    ) => {
        if (ordini.length === 0) return null;
        return (
            <>
                {/*<Typography*/}
                {/*    variant="caption"*/}
                {/*    color="text.secondary"*/}
                {/*    sx={{display: "block", mb: 0.5}}*/}
                {/*>*/}
                {/*    {titolo} ({ordini.length})*/}
                {/*</Typography>*/}
                <Chip
                    label={`${titolo} (${ordini.length})`}
                    color={colore}
                    size="small"
                    variant="outlined"
                    sx={{
                        mb: 1,
                        fontWeight: "bold",
                        width: "100%",
                        borderRadius: 1,
                    }}
                />
                {ordini.map((ordineLavoro) => (
                    <GanttOrdineLavoroItem
                        key={ordineLavoro.id}
                        ordineLavoro={ordineLavoro}
                        isSelected={ordineLavoro.id === ordineLavoroSelezionatoId}
                        onClick={onOrdineLavoroClick}
                    />
                ))}
            </>
        );
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <CardHeader
                title={
                    <Box>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold">
                                Ordini di Lavoro
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={mostraTutti}
                                        onChange={handleToggleMostraTutti}
                                    />
                                }
                                label={
                                    <Typography variant="caption">
                                        {mostraTutti ? "Tutti" : "Da schedulare"}
                                    </Typography>
                                }
                                labelPlacement="start"
                                sx={{mr: 0}}
                            />
                        </Stack>


                        <Grid container spacing={1}>
                            {/* Riga 1: Data Inizio */}
                            <Grid size={{xs: 12}}>
                                <DesktopDatePicker
                                    label="Data Inizio"
                                    value={dataDa}
                                    onChange={(newValue: DateTime | null) => {
                                        setDataDa(newValue || null);
                                        setRefresh(true);
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                        },
                                        field: {
                                            clearable: true,
                                        },
                                    }}
                                />
                            </Grid>
                            {/* Riga 2: Data Fine */}
                            <Grid size={{xs: 12}}>
                                <DesktopDatePicker
                                    label="Data Fine"
                                    value={dataA}
                                    onChange={(newValue: DateTime | null) => {
                                        setDataA(newValue || null);
                                        setRefresh(true);
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                        },
                                        field: {
                                            clearable: true,
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Riga 2: Commerciale */}
                            <Grid size={{xs: 12}}>
                                <Autocomplete
                                    value={commerciali.find((c) => c.id === commercialeSel) || null}
                                    options={commerciali}
                                    getOptionLabel={(option) => option.user_display || ""}
                                    size="small"
                                    onChange={(_, newValue) => {
                                        setCommercialeSel(newValue?.id);
                                        setRefresh(true);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            sx={{
                                                backgroundColor: commercialeSel
                                                    ? theme.palette.info.main + '14'
                                                    : "inherit",
                                            }}
                                            label="Commerciale"
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Riga 3: Chips stati */}
                            <Grid size={{xs: 12}}>
                                {statiSelezionati.map((stato) => (
                                    <Chip
                                        key={stato}
                                        label={STATI_ORDINE_LAVORO[stato as keyof typeof STATI_ORDINE_LAVORO]}
                                        color={getStatoOrdineLavoroColor(stato)}
                                        size="small"
                                        onDelete={() => {
                                            toggleStato(stato);
                                            setRefresh(true);
                                        }}
                                    />
                                ))}
                                <IconButton
                                    size="small"
                                    onClick={(e) => setAnchorElStatoFilter(e.currentTarget)}
                                    sx={{
                                        color: statiSelezionati.length > 0
                                            ? "info.main"
                                            : "inherit",
                                    }}
                                >
                                    <FilterAltIcon fontSize="small"/>
                                </IconButton>
                            </Grid>
                        </Grid>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Totale: {ordiniLavoroList.count} record
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {countVisualizzati} ordini visualizzati
                            </Typography>
                        </Stack>
                    </Box>
                }
                sx={{background: theme.palette.background.default}}
            />
            <Menu
                anchorEl={anchorElStatoFilter}
                open={Boolean(anchorElStatoFilter)}
                onClose={() => setAnchorElStatoFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {Object.entries(STATI_ORDINE_LAVORO).map(([key, value]) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={statiSelezionati.includes(key)}
                                    onChange={() => toggleStato(key)}
                                    color="info"
                                />
                            }
                            label={value}
                        />
                    ))}
                </FormGroup>
                <Box sx={{px: 2, pb: 1}}>
                    <Button
                        onClick={() => {
                            setAnchorElStatoFilter(null);
                            setRefresh(true);
                        }}
                        variant="contained"
                        color="info"
                        size="small"
                        fullWidth
                    >
                        Applica
                    </Button>
                </Box>
            </Menu>
            <CardContent
                sx={{
                    p: 1,
                    flex: 1,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        pr: 0.5,
                    }}
                >
                    {isLoading ? (
                        <Stack
                            sx={{
                                height: 200,
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress size={32}/>
                        </Stack>
                    ) : countVisualizzati === 0 ? (
                        <Box sx={{textAlign: "center", py: 4}}>
                            <Typography variant="body2" color="text.secondary">
                                {mostraTutti
                                    ? "Nessun ordine di lavoro nel periodo"
                                    : "Nessun ordine da schedulare nel periodo"
                                }
                            </Typography>
                        </Box>
                    ) : mostraTutti ? (
                        /* ===== VISTA "TUTTI" ===== */
                        <>
                            {renderSezione("Da schedulare", ordiniDaSchedulare, "warning")}

                            {ordiniDaSchedulare.length > 0 && ordiniSchedulati.length > 0 && (
                                <Divider sx={{my: 1.5, borderBottomWidth: 2}}/>
                            )}

                            {renderSezione("Schedulati", ordiniSchedulati, "info")}

                            {(ordiniDaSchedulare.length > 0 || ordiniSchedulati.length > 0) &&
                                ordiniProdotti.length > 0 && (
                                    <Divider sx={{my: 1}}/>
                                )}

                            {renderSezione("Prodotti", ordiniProdotti, "success")}
                        </>
                    ) : (
                        /* ===== VISTA "DA SCHEDULARE" ===== */
                        <>
                            {ordiniDaSchedulare.map((ordineLavoro) => (
                                <GanttOrdineLavoroItem
                                    key={ordineLavoro.id}
                                    ordineLavoro={ordineLavoro}
                                    isSelected={ordineLavoro.id === ordineLavoroSelezionatoId}
                                    onClick={onOrdineLavoroClick}
                                />
                            ))}
                        </>
                    )}
                </Box>
            </CardContent>
            {(ordiniLavoroList.num_pages > 1 || ordiniLavoroList.count > 10) && (
                <Stack
                    direction="column"
                    spacing={2}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Pagination
                        count={ordiniLavoroList.num_pages}
                        page={filtriOrdiniLavoro.page || 1}
                        onChange={(event: React.ChangeEvent<unknown>, page: number) => {
                            setPage(page)
                            setRefresh(true)
                        }}
                        color="primary"
                    />

                    <FormControl sx={{minWidth: 120}} size="small">
                        <InputLabel>Record per pagina</InputLabel>
                        <Select
                            value={filtriOrdiniLavoro.numero_record || 20}
                            label="Record per pagina"
                            onChange={(event: SelectChangeEvent<number>) => {
                                const numero_record = event.target.value as number;
                                setNumeroRecord(numero_record);
                                setRefresh(true);
                            }}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            )}
        </Card>

    );
};

export default GanttOrdiniLavoroCard;